import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import axios from '../../axiosConfig';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import { fetchMyStore } from '../store/storeSlice';
import { fetchOnboardingProgress } from '../onboarding/onboardingSlice';
import { fetchProducts } from '../products/productSlice';
import Shell from '../dashboard/Shell';
import KYCProgressCard from '../../components/KYCProgressCard';
import CalculatorRecentCard from '../utilities/calculator/CalculatorRecentCard';
import NotesRecentCard from '../utilities/notes/NotesRecentCard';
import TasksRecentCard from '../utilities/tasks/TasksRecentCard';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { useToast } from '../../components/ui/Toast';
import { getStoreUrl } from '../../config/platformConfig';
import {
  CheckCircle2,
  Circle,
  Copy,
  ArrowRight,
  ExternalLink,
  HelpCircle,
  MessageCircle,
  Bell,
  CreditCard,
  Truck,
  Building2,
  Package,
  Palette,
  Gift,
  IdCard,
  BellRing,
  Plug,
  Sparkles,
  Crown,
  Smartphone,
  Globe,
  QrCode,
  Check,
} from 'lucide-react';
import Modal from '../../components/ui/Modal';
import { QRCodeDisplay } from '../../components/QRCode';
import './Overview.css';

const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.revstar.merchant';
const MOBILE_INSTALLED_KEY = 'revstarMobileAppInstalled';

/**
 * Overview — the merchant's primary landing surface post-onboarding.
 *
 * Splits cleanly from /dashboard:
 *   Overview = welcome + KYC + setup progress + store URL + help (lifecycle)
 *   Dashboard = stats + chart + inventory alerts + top products (operations)
 *
 * Mobile-first: single column, right-rail content stacks below the main
 * column. At 1024px+ we switch to a two-column grid (main + 320px rail).
 */

// Each setup item: a single checklist row. Completed → green check + "Completed".
// Pending → empty circle + contextual CTA link in brand color.
// Locked (feature-gated, tier doesn't have access) → PRO pill instead of CTA;
// click opens the upgrade page rather than the gated destination.
const SetupItem = ({ icon: ItemIcon, title, completed, ctaLabel, ctaTo, onClick, locked }) => (
  <div className={`ov-setup-item ${completed ? 'ov-setup-item-done' : ''} ${locked ? 'ov-setup-item-locked' : ''}`}>
    <div className="ov-setup-item-left">
      {completed ? (
        <CheckCircle2 size={20} className="ov-setup-check-done" />
      ) : (
        <Circle size={20} className="ov-setup-check-pending" />
      )}
      <span className="ov-setup-item-title">{title}</span>
    </div>
    {completed ? (
      <span className="ov-setup-status-done">Completed</span>
    ) : locked ? (
      <Link to="/settings?tab=billing" className="ov-setup-cta ov-setup-cta-pro">
        <span>PRO</span>
      </Link>
    ) : ctaTo ? (
      <Link to={ctaTo} className="ov-setup-cta" onClick={onClick}>
        <span>{ctaLabel}</span>
        {ItemIcon ? <ItemIcon size={14} /> : <ArrowRight size={14} />}
      </Link>
    ) : (
      <button type="button" className="ov-setup-cta" onClick={onClick}>
        <span>{ctaLabel}</span>
        {ItemIcon ? <ItemIcon size={14} /> : <ArrowRight size={14} />}
      </button>
    )}
  </div>
);

const Overview = () => {
  const dispatch = useDispatch();
  const toast = useToast();
  const { isAuthenticated, user } = useSecureAuth();
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);

  // storeSlice writes the active store to state.store.current (slice + field
   // name collide, same shape gotcha noted for products below). The previous
   // `state.store?.myStore` selector was always undefined, so storefrontUrl
   // resolved to null and the chip + the right-rail "Your store is live"
   // card silently never rendered.
  const store = useSelector((state) => state.store?.current);
  // productSlice writes the list to state.products.products (slice name +
  // field name collide). The previous `state.products.list` selector was
  // always undefined, so this row never ticked.
  const products = useSelector((state) => state.products?.products || []);
  const onboarding = useSelector((state) => state.onboarding);

  // "Payment is set up" really means at least one gateway is enabled in
  // /settings?tab=payments. The store row's paymentSettings field doesn't
  // reflect gateway activation, so fetch the gateway list directly. Cheap
  // call, runs once per Overview mount.
  const [paymentActive, setPaymentActive] = useState(false);
  // "Shipping is set up" = merchant has at least one shipping zone.
  const [shippingActive, setShippingActive] = useState(false);
  // "AI notifications enabled" = preferences.aiNotificationsEnabled === true.
  // Backend GET /notifications/preferences is gated on notifications.ai
  // (PRO+); FREE merchants 403 here, which is fine — they see the locked
  // PRO pill instead of a tickable row.
  const [aiNotificationsEnabled, setAiNotificationsEnabled] = useState(false);

  // Storefront chatbot (livechat) activation state. Endpoint lazy-creates
  // a default row so this returns isEnabled=false for first-time merchants.
  const [livechatEnabled, setLivechatEnabled] = useState(false);

  // "Mobile app installed" can't be detected from the web — we trust a
  // localStorage flag the merchant sets via the "I've installed it" CTA
  // in the post-login modal or this row.
  const [mobileInstalled, setMobileInstalled] = useState(() => {
    try {
      return localStorage.getItem(MOBILE_INSTALLED_KEY) === 'true';
    } catch {
      return false;
    }
  });

  // Tier check for the AI-notifications row's lock state.
  const { hasAccess: hasAiNotifications, loading: aiNotifLoading } = useFeatureAccess('notifications.ai');
  const aiNotifLocked = !aiNotifLoading && !hasAiNotifications;

  // Tier check for the livechat row's lock state. sales.storefront_chatbot
  // is PRO/PREMIUM; FREE merchants get a PRO pill that routes to billing.
  const { hasAccess: hasLivechat, loading: livechatLoading } = useFeatureAccess('sales.storefront_chatbot');
  const livechatLocked = !livechatLoading && !hasLivechat;

  // AI assistant is tier-gated. FREE merchants see the row with a "PRO"
  // badge; click routes to the upgrade modal/page rather than the AI page.
  // Using `ai.predictions` (PRO/PREMIUM) since that's the most accessible
  // AI feature seed.
  const { hasAccess: hasAI, loading: aiLoading } = useFeatureAccess('ai.predictions');
  const aiLocked = !aiLoading && !hasAI;

  // Load the data the checklist depends on. Each fetch is cheap and idempotent;
  // Redux already handles dedupe via slice-level loading flags.
  useEffect(() => {
    if (isAuthenticated && user) {
      dispatch(fetchMyStore());
      dispatch(fetchOnboardingProgress());
      dispatch(fetchProducts());

      let cancelled = false;
      axios
        .get('/merchant/payment-gateways/gateways')
        .then((res) => {
          const gateways = Array.isArray(res.data?.data) ? res.data.data : [];
          // Backend returns each gateway with `enabled: boolean` (Settings.js
          // uses the same field). The `status: 'active'` shape that
          // MerchantPayments.js checks doesn't exist in this response.
          if (!cancelled) {
            setPaymentActive(gateways.some((g) => g.enabled === true));
          }
        })
        .catch(() => {
          // 404/network — leave paymentActive false, checklist row stays pending
        });

      // Shipping zones — backend returns { zones: [...], total: N }.
      axios
        .get('/shipping-zones')
        .then((res) => {
          const zones = Array.isArray(res.data?.zones) ? res.data.zones : [];
          if (!cancelled) {
            setShippingActive(zones.length > 0);
          }
        })
        .catch(() => {});

      // AI notification preferences. Endpoint is feature-gated; FREE merchants
      // get a 403 which we silently swallow (the row stays in locked state).
      axios
        .get('/notifications/preferences')
        .then((res) => {
          const prefs = res.data?.preferences;
          if (!cancelled && prefs) {
            setAiNotificationsEnabled(prefs.aiNotificationsEnabled === true);
          }
        })
        .catch(() => {});

      // Storefront chatbot settings — endpoint lazy-creates a default row so
      // this is safe to call for any tier. FREE merchants still get a valid
      // response with isEnabled=false (the row uses tier gating, not 403s).
      axios
        .get('/merchant/chatbot/settings')
        .then((res) => {
          const isEnabled =
            res.data?.settings?.isEnabled ?? res.data?.isEnabled ?? false;
          if (!cancelled) setLivechatEnabled(Boolean(isEnabled));
        })
        .catch(() => {});

      return () => { cancelled = true; };
    }
  }, [isAuthenticated, user, dispatch]);

  const handleInstallMobile = () => {
    window.open(PLAY_STORE_URL, '_blank', 'noopener,noreferrer');
    try {
      localStorage.setItem(MOBILE_INSTALLED_KEY, 'true');
    } catch {}
    setMobileInstalled(true);
  };

  // Build the checklist from real signals. Each "completed" boolean is derived
  // from store/user state, NOT from a separate progress endpoint, so the UI
  // can't drift from reality.
  const setupItems = useMemo(() => {
    const hasProducts = Array.isArray(products) && products.length > 0;
    const hasStore = !!store?.id;
    const hasPayment = paymentActive;
    const hasShipping = shippingActive;
    const hasTheme = !!(store?.theme && store.theme !== 'MODERN') || !!(store?.themeConfig && Object.keys(store.themeConfig).length > 0);
    const hasNotifications = aiNotificationsEnabled;

    return [
      {
        key: 'account',
        title: 'Create account',
        completed: !!user,
        ctaLabel: 'Sign in',
        ctaTo: '/login',
      },
      {
        key: 'business',
        title: 'Setup your business',
        completed: hasStore,
        ctaLabel: 'Set up',
        ctaTo: '/manage-store/information',
        icon: Building2,
      },
      {
        key: 'product',
        title: 'Add your first product',
        completed: hasProducts,
        ctaLabel: 'Add product',
        ctaTo: '/products',
        icon: Package,
      },
      {
        key: 'payment',
        title: 'Setup payment',
        completed: hasPayment,
        ctaLabel: 'Set up',
        ctaTo: '/settings?tab=payments',
        icon: CreditCard,
      },
      {
        key: 'shipment',
        title: 'Setup shipment',
        completed: hasShipping,
        ctaLabel: 'Create',
        ctaTo: '/shipping-zones',
        icon: Truck,
      },
      {
        key: 'theme',
        title: 'Customize website',
        completed: hasTheme,
        ctaLabel: 'Customize',
        ctaTo: '/manage-store/customization',
        icon: Palette,
      },
      {
        key: 'notifications',
        title: 'Enable AI notifications',
        completed: hasNotifications,
        ctaLabel: 'Enable',
        ctaTo: '/notifications/preferences',
        locked: aiNotifLocked,
        icon: Bell,
      },
      {
        key: 'livechat',
        title: 'Activate Livechat',
        completed: livechatEnabled,
        ctaLabel: 'Activate',
        ctaTo: '/storefront-chat/settings',
        locked: livechatLocked,
        icon: MessageCircle,
      },
      {
        key: 'mobile',
        title: 'Install the mobile app',
        completed: mobileInstalled,
        ctaLabel: 'Install',
        onClick: handleInstallMobile,
        icon: Smartphone,
      },
    ];
  }, [
    user,
    store,
    products,
    paymentActive,
    shippingActive,
    aiNotificationsEnabled,
    aiNotifLocked,
    livechatEnabled,
    livechatLocked,
    mobileInstalled,
  ]);

  const completedCount = setupItems.filter((i) => i.completed).length;
  const totalCount = setupItems.length;
  const progressPercent = Math.round((completedCount / totalCount) * 100);

  // Store URL for the live-store pill on the right rail.
  const storefrontUrl = store?.slug ? getStoreUrl(store.slug) : null;

  const handleCopyUrl = () => {
    if (!storefrontUrl) return;
    navigator.clipboard.writeText(storefrontUrl).then(
      () => {
        setCopied(true);
        toast.success('Store URL copied');
        setTimeout(() => setCopied(false), 1500);
      },
      () => toast.error('Could not copy — please try again')
    );
  };

  const merchantName = user?.firstName || user?.username || user?.businessName || 'there';

  return (
    <Shell>
      <div className="ov-container">
        {/* Header */}
        <header className="ov-header">
          <h1 className="ov-title">
            Hello {merchantName} <span className="ov-wave" aria-hidden="true">👋</span>
          </h1>
          <p className="ov-subtitle">Here's everything you need to do to set up your store.</p>

          {/* Storefront access chip — compact pill with click-to-visit,
              copy URL, and QR popover. Always visible above the fold so
              merchants can hop to their store without hunting in the
              right rail (which falls below the fold on mobile). */}
          {storefrontUrl && (
            <div className="ov-storelink">
              <Globe size={14} className="ov-storelink-icon" aria-hidden="true" />
              <a
                href={storefrontUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="ov-storelink-url"
                title={storefrontUrl}
              >
                {storefrontUrl.replace(/^https?:\/\//, '')}
              </a>
              <button
                type="button"
                className="ov-storelink-act"
                onClick={handleCopyUrl}
                title={copied ? 'Copied' : 'Copy link'}
                aria-label="Copy URL"
              >
                {copied ? <Check size={13} /> : <Copy size={13} />}
              </button>
              <button
                type="button"
                className="ov-storelink-act"
                onClick={() => setShowQR(true)}
                title="QR code"
                aria-label="Show QR code"
              >
                <QrCode size={13} />
              </button>
              <a
                href={storefrontUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="ov-storelink-act ov-storelink-visit"
                title="Open storefront"
                aria-label="Open storefront"
              >
                <ExternalLink size={13} />
              </a>
            </div>
          )}
        </header>

        {/* Two-column layout: main + right rail (stacks on mobile) */}
        <div className="ov-layout">
          <main className="ov-main">
            {/* KYC Progress (existing component, just relocated here) */}
            <KYCProgressCard />

            {/* Setup checklist — the centerpiece of Overview */}
            <section className="ov-card ov-setup-card">
              <header className="ov-setup-header">
                <div>
                  <h2 className="ov-card-title">Finish setting up</h2>
                  <p className="ov-card-subtitle">Follow these tips to get started.</p>
                </div>
                <div className="ov-setup-progress">
                  <span className="ov-setup-progress-value">{progressPercent}%</span>
                </div>
              </header>
              <div
                className="ov-setup-progress-bar"
                role="progressbar"
                aria-valuenow={progressPercent}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div className="ov-setup-progress-bar-fill" style={{ width: `${progressPercent}%` }} />
              </div>
              <div className="ov-setup-list">
                {setupItems.map(({ key, ...rest }) => (
                  <SetupItem key={key} {...rest} />
                ))}
              </div>
            </section>

            {/* Help cards — two-up on tablet+, one-up on mobile */}
            <div className="ov-help-grid">
              <Link to="/knowledgebase" className="ov-help-card">
                <div className="ov-help-icon" aria-hidden="true">
                  <HelpCircle size={20} strokeWidth={2} />
                </div>
                <div className="ov-help-body">
                  <h3 className="ov-help-title">Got questions? Find answers here</h3>
                  <p className="ov-help-text">
                    Browse our knowledgebase for setup guides, payment walkthroughs, and more.
                  </p>
                  <span className="ov-help-cta ov-help-cta-orange">
                    Find answers <ArrowRight size={14} />
                  </span>
                </div>
              </Link>

              <Link to="/community" className="ov-help-card">
                <div className="ov-help-icon" aria-hidden="true">
                  <MessageCircle size={20} strokeWidth={2} />
                </div>
                <div className="ov-help-body">
                  <h3 className="ov-help-title">Need help?</h3>
                  <p className="ov-help-text">
                    Join our community of merchants and suppliers — share wins, get advice, find collaborators.
                  </p>
                  <span className="ov-help-cta ov-help-cta-primary">
                    Join community <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            </div>
          </main>

          {/* Right rail — supporting cards. Stacks below main on mobile. */}
          <aside className="ov-rail">
            {storefrontUrl && (
              <section className="ov-card ov-store-live">
                <div className="ov-store-live-status">
                  <span className="ov-store-live-dot" aria-hidden="true" />
                  <span className="ov-store-live-label">Your store is live</span>
                </div>
                <a
                  href={storefrontUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ov-store-live-url"
                >
                  {storefrontUrl}
                </a>
                <div className="ov-store-live-actions">
                  <button
                    type="button"
                    className="ov-store-live-btn ov-store-live-btn-secondary"
                    onClick={handleCopyUrl}
                    aria-label="Copy store URL"
                    title="Copy store URL"
                  >
                    <Copy size={16} />
                    <span>{copied ? 'Copied' : 'Copy'}</span>
                  </button>
                  <a
                    href={storefrontUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ov-store-live-btn ov-store-live-btn-primary"
                    aria-label="Open storefront"
                  >
                    <span>Open</span>
                    <ExternalLink size={16} />
                  </a>
                </div>
              </section>
            )}

            {/* Refer & Earn — flat brand-teal pill, copy + view buttons.
                Salesive's pattern: a single highlighted earn surface that
                stands out without dominating the rail. */}
            <section className="ov-card ov-refer">
              <div className="ov-refer-icon" aria-hidden="true">
                <Gift size={20} strokeWidth={2} />
              </div>
              <div className="ov-refer-body">
                <h3 className="ov-refer-title">Refer & earn 30%</h3>
                <p className="ov-refer-text">Share your link to earn.</p>
              </div>
              <Link to="/referrals" className="ov-refer-cta" aria-label="Open referrals">
                View
                <ArrowRight size={14} />
              </Link>
            </section>

            {/* Quick Actions — utility shortcuts. Each item is a link with
                a small tinted icon. Calculator/Note/Calendar/AI Assistant
                mirror Salesive's "Utilities" panel. */}
            <section className="ov-card ov-quick">
              <header className="ov-quick-header">
                <Package size={16} strokeWidth={2} className="ov-quick-header-icon" />
                <h3 className="ov-quick-title">Quick actions</h3>
              </header>
              <div className="ov-quick-list">
                <Link to="/business-card" className="ov-quick-item">
                  <span className="ov-quick-item-icon" aria-hidden="true">
                    <IdCard size={16} strokeWidth={2} />
                  </span>
                  <div className="ov-quick-item-body">
                    <span className="ov-quick-item-title">Business card</span>
                    <span className="ov-quick-item-text">Share your digital card</span>
                  </div>
                </Link>
                <Link to="/utilities/tasks" className="ov-quick-item">
                  <span className="ov-quick-item-icon" aria-hidden="true">
                    <BellRing size={16} strokeWidth={2} />
                  </span>
                  <div className="ov-quick-item-body">
                    <span className="ov-quick-item-title">Reminder</span>
                    <span className="ov-quick-item-text">Email pings for any task</span>
                  </div>
                </Link>
                <Link to="/settings?tab=integrations" className="ov-quick-item">
                  <span className="ov-quick-item-icon" aria-hidden="true">
                    <Plug size={16} strokeWidth={2} />
                  </span>
                  <div className="ov-quick-item-body">
                    <span className="ov-quick-item-title">Integrations</span>
                    <span className="ov-quick-item-text">Connect Google Calendar &amp; more</span>
                  </div>
                </Link>
                {/* AI Assistant — feature-gated. When the merchant lacks
                    `ai.predictions` access, the row shows with a PRO badge
                    and routes to /upgrade instead of /ai-analytics. */}
                <Link
                  to={aiLocked ? '/upgrade' : '/ai-analytics'}
                  className={`ov-quick-item ${aiLocked ? 'ov-quick-item-locked' : ''}`}
                >
                  <span className="ov-quick-item-icon" aria-hidden="true">
                    <Sparkles size={16} strokeWidth={2} />
                  </span>
                  <div className="ov-quick-item-body">
                    <span className="ov-quick-item-title-row">
                      <span className="ov-quick-item-title">AI assistant</span>
                      {aiLocked && <span className="ov-quick-pro-badge">PRO</span>}
                    </span>
                    <span className="ov-quick-item-text">
                      {aiLocked ? 'Upgrade to use AI insights' : 'Ask anything about your store'}
                    </span>
                  </div>
                </Link>
              </div>
            </section>

            {/* Utility widgets — recent items per tool. Stack below Quick
                Actions so frequently-used links stay near the top of the
                rail and content widgets grow downward. */}
            <TasksRecentCard />
            <NotesRecentCard />
            <CalculatorRecentCard />

            {/* Trial countdown — only shown for merchants on a trial plan.
                Pinned at the bottom of the rail so it's the last thing the
                merchant sees, with an obvious Upgrade CTA. */}
            {user?.trialEndsAt && new Date(user.trialEndsAt) > new Date() && (
              <section className="ov-card ov-trial">
                <div className="ov-trial-header">
                  <Crown size={16} strokeWidth={2} />
                  <span className="ov-trial-label">Free trial</span>
                  <span className="ov-trial-days">
                    {Math.max(0, Math.ceil((new Date(user.trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24)))} days left
                  </span>
                </div>
                <Link to="/upgrade" className="ov-trial-cta">Upgrade</Link>
              </section>
            )}
          </aside>
        </div>

        {/* QR popover — opened from the header storefront chip. Reuses
            QRCodeDisplay (already used in Settings → General). */}
        <Modal
          isOpen={showQR}
          onClose={() => setShowQR(false)}
          title="Store QR code"
          size="sm"
        >
          <div className="ov-qr-wrap">
            <QRCodeDisplay type="store" size={220} showUrl showActions />
          </div>
        </Modal>
      </div>
    </Shell>
  );
};

export default Overview;
